import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { Observable, forkJoin, throwError, Subscription, interval, takeWhile, EMPTY, lastValueFrom, take } from "rxjs";
import * as _ from 'lodash';
import { map, mergeMap, catchError, switchMap, startWith, skipWhile, tap } from "rxjs/operators";
import { FileService, User, CacheService, ToastrService, ITypeStatusRecommendationBO, ITypeCritiqueFactBO, ITypeStatusActionPlanBO, IRecommendationImpactTypeBO, IRecommendationProbabilityTypeBO, ITypeRiskBO, IRecommendationBO, IRecommendationCommentBO, IRecommendationActionPlanBO } from '@mapfre-tech/amap3';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { truncate } from "fs";

@Injectable({
   providedIn: "root"
})
export class RecommendationService {

   className = 'RecommendationService';

   constructor(private http: HttpClient, private fileService: FileService, private cacheService: CacheService,
      private toastService: ToastrService, private translateService: TranslateService) { }

   //!NO se usa
   getRecommendationByFactObserverId(factObserverId: number, jobId: number): Observable<IRecommendationBO[]> {
      return this.http.get<IRecommendationBO[]>(environment.base_URL + "/recommendation", { params: { factObserverId: factObserverId, jobId } });
   }

   getRecommendationById(id: number): Observable<IRecommendationBO[]> {
      return this.http.get<IRecommendationBO[]>(environment.base_URL_GAIA + `/recommendation/${id}`);
   }

   getImpactTypesRecommendation(cache = true, cacheKey = "recImpactTypes"): Observable<IRecommendationImpactTypeBO[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<IRecommendationImpactTypeBO[]>;
       } else {
         const observable = this.http.get<IRecommendationImpactTypeBO[]>(environment.base_URL + '/recommendation/impact-types').pipe(map((res) => {
         this.cacheService.updateCacheData(res, cacheKey)
         return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   getProbabilityTypesRecommendation(cache = true, cacheKey = "recProbabilityTypes"): Observable<IRecommendationProbabilityTypeBO[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<IRecommendationProbabilityTypeBO[]>;
       } else {
         const observable = this.http.get<IRecommendationProbabilityTypeBO[]>(environment.base_URL + '/recommendation/probability-types').pipe(map((res) => {
         this.cacheService.updateCacheData(res, cacheKey)
         return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   exportRecommendation(key: string, params: any) {

      const user_locale = window.navigator.language;
      _.set(params, "locale", user_locale);

      _.set(params, "key", key + '/data.json');

      this.toastService.add({ severity: "info", summary: this.translateService.instant("TOAST.GENERAL.INFO_LOADING_TITLE"), detail: this.translateService.instant("TOAST.GENERAL.INFO_EXPORT"), life: 240000 });

      // Call aws gateway to generate pre signed url
      return this.http.get<any[]>(environment.base_URL + "/exported-url", { params })
         .pipe(mergeMap((res: any) => {

            // Remove key paramenter and add url parameter
            _.unset(params, "key")
            _.set(params, "url", res)

            // Call Mapgre Gateway to generate excel with exoprted jobs
            return this.http.get<any[]>(environment.base_URL + "/exported-recommendations", { params })
               .pipe(mergeMap((res: any) => {

                  // Remove url paramenter and add key parameter
                  const paramsExcel = {}
                  _.set(paramsExcel, "keydata", key + '/data.json');
                  _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

                  return this.http.get<any[]>(environment.base_URL + "/create-excel", { params: paramsExcel })
                     .pipe(mergeMap((res3: any) => {

                        // Remove url paramenter and add key parameter
                        const paramsDownload = {}
                        _.set(paramsDownload, "onlyDownload", true)

                        const body = {
                              key: key + '/export.xlsx'
                            }

                        // Download excel
                        return this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                           .pipe(mergeMap((res: any) => {

                              // Download file
                              const options = {
                                 responseType: "blob" as 'json'
                              }

                              // Download file form S3
                              return this.http.get(res, options);

                           }), map(res => {

                              //Clear toast notification
                              this.toastService.clear()
                              //Download generated Excel
                              FileSaver.saveAs(res, `export.xlsx`);

                              // Delete file when downloaded
                              this.delete(key).subscribe(() => { });
                              return res;

                           }), catchError(err => {
                              this.toastService.add({
                                 severity: "error",
                                 summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                                 detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                              });
                              return throwError(err);
                           }))
                     }))
               }))

         }), map((res: any) => {
            return res;
         }), catchError<Observable<any>, any>((err) => {

            if(err.status != 500){ 

               const target = {};
               _.set(target, "keyFile", key + '/data.json');

               let ob: Observable<any>;
               let cont = 0;

               //Condiciones de timeout y repetición de request a check-files
               function checkConditions(response: any){
                  if(cont <18 && response.statusCode === 404){
                     cont++;
                     return true;
                  }else{
                     cont++;
                     return false
                  }
               }

               //Multiple request to lambda chech-files. Stops when file has been found or timeout
               ob = interval(5000)
               .pipe(
               startWith(0),
               switchMap(() => this.http.get<any>(environment.base_URL + "/check-files", { params: target })),
               takeWhile(response => checkConditions(response), true)
               )

               forkJoin([ob]).subscribe(([check]) => {

                  if(check.statusCode === 200){

                     const paramsExcel = {}
                     _.set(paramsExcel, "keydata", key + '/data.json');
                     _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

                     const result = forkJoin( [this.http.get<any[]>(environment.base_URL + "/create-excel", { params: paramsExcel })])

                     return forkJoin([result]).subscribe(([resul1]) => {

                        // Remove url paramenter and add key parameter
                        const paramsDownload = {}
                        _.set(paramsDownload, "onlyDownload", true)

                        const body = {
                           key: key + '/export.xlsx'
                        }

                        // Download excel
                        const result2 = this.http.post(environment.base_URL + `/download`, body, { params: paramsDownload })
                           .pipe(mergeMap((res: any) => {
                              // Download file
                              const options = {
                                 responseType: "blob" as 'json'
                              }

                              //Clear toast notification
                              this.toastService.clear()

                              // Download file form S3
                              return this.http.get(res, options);

                           }), map(res => {

                              // Delete file when downloaded
                              this.delete(key).subscribe(() => { });
                              return res;

                           }), catchError(err => {
                              this.toastService.add({
                                 severity: "error",
                                 summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                                 detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                              });
                              return throwError(err);
                           }))

                           return forkJoin([result2]).subscribe(([resul2]) => {
                              //Download generated Excel
                              FileSaver.saveAs(resul2, `export.xlsx`);
                              return resul2;
                           });
                     });
                  }else{
                     //Clear toast info notification
                     this.toastService.clear()

                     this.toastService.add({
                        severity: "error",
                        summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                        detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TIMEOUT"), life: 5000
                     });
                  }
            });
            }else{
               //Clear toast info notification
               setTimeout(() => {
                  this.toastService.clear()
               },4000)
            }

         return EMPTY;
         }))
   }

   delete(key: string) {
      const params = {
         key
      }
      return this.http.post(environment.base_URL + "/delete", { params }).pipe(catchError(err => {
         this.toastService.add({
            severity: "error",
            summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
            detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
         });
         return throwError(err);
      }));
   }

   comment(comment: IRecommendationCommentBO): Observable<IRecommendationCommentBO> {
      return this.http.post(environment.base_URL_GAIA + `/recommendation/comment`, comment);
   }

   getCommentsRecommendaionById(id: number[]): Observable<IRecommendationCommentBO[]> {
      const params = {
         recommendationIds: id.join(',')
      };
      return this.http.get<IRecommendationCommentBO[]>(environment.base_URL_GAIA + `/recommendations/comments`, { params });
   }

   getAllRecommendations(select = false, cache=true, cacheKey = "allRecommendations"): Observable<IRecommendationBO[]> {
      if (this.cacheService.canUseCache(cacheKey, [select ? 'lite' : 'full']) && cache) {
         return this.cacheService.getCacheData(cacheKey, [select ? 'lite' : 'full']) as object as Observable<IRecommendationBO[]>;
      } else {
         const observable = this.http.get<IRecommendationBO[]>(environment.base_URL + "/recommendation", { params: { select: select } }).pipe(map((res) => {
            if (cache) {
               this.cacheService.updateCacheData(res, cacheKey, [select ? 'lite' : 'full'])
            }
            return res;
         }))
         if (cache) {
            this.cacheService.updateCacheReq(cacheKey, [select ? 'lite' : 'full']);
         }
         return observable;
      }
   }

   removeRecommendationByProgramJobId(programJobId: number, code: string): Observable<IRecommendationBO[]> {
      return this.http.delete<IRecommendationBO[]>(environment.base_URL + "/recommendation/" + programJobId + "/code/" + code);
   }

   createRecommendation(recommendation: IRecommendationBO[]): Observable<IRecommendationBO[]> {
      return this.http.post<IRecommendationBO[]>(environment.base_URL + "/recommendation", recommendation);
   }

   updateRecommendation(recommendation: IRecommendationBO[]): Observable<IRecommendationBO[]> {
      return this.http.put<IRecommendationBO[]>(environment.base_URL_GAIA + "/recommendation", recommendation);
   }

   getTypeCritiqueRecommendation(cache = true, cacheKey = 'typeCritique'): Observable<ITypeCritiqueFactBO[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeCritiqueFactBO[]>;
      } else {
         const observable = this.http.get<ITypeCritiqueFactBO[]>(environment.base_URL + "/types-critique-recommendation").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }

   }

   getTypeStatusRecommendation(cache = true, cacheKey = "typeStatus"): Observable<ITypeStatusRecommendationBO[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeStatusRecommendationBO[]>;
      } else {
         const observable = this.http.get<ITypeStatusRecommendationBO[]>(environment.base_URL + "/types-status-recommendation").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }
   }

   getTypeStatusActionPlan(cache = true, cacheKey = "typeStatusActionPlan"): Observable<ITypeStatusActionPlanBO[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeStatusActionPlanBO[]>;
      } else {
         const observable = this.http.get<ITypeStatusActionPlanBO[]>(environment.base_URL + "/types-status-action-plan").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }
   }

   getTypeRiskRecommendation(cache = true, cacheKey = "typeRisk"): Observable<ITypeRiskBO[]> {

      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<ITypeRiskBO[]>;
      } else {
         const observable = this.http.get<ITypeRiskBO[]>(environment.base_URL + "/types-risk").pipe(map((res) => {
            this.cacheService.updateCacheData(res, cacheKey)
            return res;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
      }

   }

   deleteRecommendation(recommendationId: number, recommendationCode: string): Observable<IRecommendationBO[]> {
      return this.http.delete<IRecommendationBO[]>(environment.base_URL + "/recommendation/" + recommendationId + "/code/" + recommendationCode);
   }

   getAllByJobId(jobId: number, cache?: any, cacheKey = "allByJobId"): Observable<IRecommendationBO[]> {

      if (this.cacheService.canUseCache(cacheKey, [jobId]) && cache) {
         return this.cacheService.getCacheData(cacheKey, [jobId]) as object as Observable<IRecommendationBO[]>;
      } else {
         const observable = this.http.get<IRecommendationBO[]>(environment.base_URL + "/recommendation", { params: { jobId: jobId } }).pipe(map((res) => {
            if (cache) {
               this.cacheService.updateCacheData(res, cacheKey, [jobId])
            }
            _.forEach(res, (r) => {
               if (_.get(r, 'actionPlan.userResponsible.idUserPk')) {
                  r.actionPlan!.userResponsible = new User(r.actionPlan!.userResponsible);
               }
            })
            return res;
         }))
         if (cache) {
            this.cacheService.updateCacheReq(cacheKey, [jobId]);
         }
         return observable;
      }


   }

   updateActionPlan(actionPlan: IRecommendationActionPlanBO[]): Observable<IRecommendationActionPlanBO[]> {
      return this.http.put<IRecommendationActionPlanBO[]>(environment.base_URL + "/recommendation/action-plan", actionPlan);
      //return of([1989,1999,2009]);
   }

   createActionPlan(actionPlan: IRecommendationActionPlanBO[]): Observable<IRecommendationActionPlanBO[]> {
      return this.http.post<IRecommendationActionPlanBO[]>(environment.base_URL + "/recommendation/action-plan", actionPlan);
      //return of([1948]);
   }

   /***
    *  Recomendation Status
    */

   newTypeStatusRecommendation(status: ITypeStatusRecommendationBO) {
      return this.http.post(environment.base_URL + "/types-status-recommendation", status).pipe(tap(() => this.cacheService.deleteCache('typeStatus')))
   }

   updateTypeStatusRecommendation(status: ITypeStatusRecommendationBO, id: number) {
      return this.http.put(environment.base_URL + `/types-status-recommendation/${id}`, status).pipe(tap(() => this.cacheService.deleteCache('typeStatus')))
   }

   /***
    *  Recomendations Critique
    */


   newTypeCritiqueRecommendation(critique: Omit<ITypeCritiqueFactBO, 'idTypeCritiqueFactPk'>) {
      return this.http.post(environment.base_URL + "/types-critique-recommendation", critique).pipe(tap(() => this.cacheService.deleteCache('typeCritique')))
   }

   updateTypeCritiqueRecommendation(critique: Omit<ITypeCritiqueFactBO, 'idTypeCritiqueFactPk'>, id: number) {
      return this.http.put(environment.base_URL + `/types-critique-recommendation/${id}`, critique).pipe(tap(() => this.cacheService.deleteCache('typeCritique')))
   }

   export(recommendation: IRecommendationBO, jobId: number) {
      const params = {
         jobId
      }
      const options = {
         params,
         responseType: "blob" as 'json'
      }
      return this.http.post(environment.base_URL_GAIA + "/recommendation/" + recommendation.idtSqn + "/export", recommendation, options);
   }

   createRecommendationWithFiles(recommendation: IRecommendationBO[]): Observable<IRecommendationBO> {
      // PRE FILES INI
      const files: any[] = recommendation?.[0]?.files ?? [];
      const filesToUpload = files.filter((f) => f instanceof File);
      recommendation[0].files = files.filter((f) => !(f instanceof File));
      // PRE FILES END
      //CHECK IF THERE ARE NEW FILES
      if (!_.isEmpty(filesToUpload)) {
         return this.createRecommendation(recommendation).pipe(mergeMap((res: any) => {
            const observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'recommendation', String(_.get(res, '[0]')), 'auditor/private'));
            return forkJoin(observables).pipe(map(resultFiles => {
               return res;
            }));
         }))
      }
      else {
         return this.createRecommendation(recommendation) as Observable<IRecommendationBO>;
      }
   }

   updateRecommendationWithFiles(recommendation: IRecommendationBO[]): Observable<IRecommendationBO> {
      // PRE FILES INI
      console.log(recommendation)
      const files: any[] = recommendation?.[0]?.files ?? [];
      const filesToUpload = files.filter((f) => f instanceof File);
      recommendation[0].files = files.filter((f) => !(f instanceof File));
      console.log(filesToUpload)
      // PRE FILES END
      //CHECK IF THERE ARE NEW FILES
      if (filesToUpload.length > 0) {
         return this.updateRecommendation(recommendation).pipe(mergeMap((res: any) => {
            const observables = filesToUpload.map((f) => this.fileService.upload(f, 'recommendation', String(recommendation[0].idtSqn), 'auditor/private'));
            return forkJoin(observables).pipe(map(resultFiles => {
               return res;
            }));
         }))
      }
      else {
         return this.updateRecommendation(recommendation) as Observable<IRecommendationBO>;
      }
   }

   getFilteredRecommendations(params: any, apply = false): Observable<IRecommendationBO[]> {
      const observable = this.http.get<IRecommendationBO[]>(environment.base_URL_GAIA + "/filtered_recommendations", { params }).pipe(map((res) => {
 
         if(params.page === 0 && apply === true){
            this.cacheService.updateCacheData(res, "rdnSearchCached")
            this.cacheService.updateCacheData(params, "filtersRdnCache")
         }
         return res;
      }));
      return observable;
   }

   /**
    * Delete Cache
    */

   deleteCache() {

      const memoryCache = ["typeCritique", "typeRisk", "allByJobId", "typeStatus", "allRecommendations", "typeStatusActionPlan"];

      _.forEach(memoryCache, item => {
         this.cacheService.deleteCache(item);
      })

   }


}
